import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Leaf } from "lucide-react";

export default function Login() {
  const { login, user, ready } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [logo, setLogo] = useState("");

  useEffect(() => {
    api.get("/public/business").then((r) => setLogo(r.data.logo_url || "")).catch(() => {});
  }, []);

  useEffect(() => {
    if (ready && user) navigate(user.role === "staff" || user.role === "crew_manager" ? "/cad" : "/", { replace: true });
  }, [ready, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Hero */}
      <div className="relative hidden lg:block">
        <img
          src="https://images.unsplash.com/photo-1766603636700-e9d80473f40f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBob3VzZSUyMGJlYXV0aWZ1bCUyMGxhd258ZW58MHx8fHwxNzgzODc3ODYyfDA&ixlib=rb-4.1.0&q=85"
          alt="Lush green lawn"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/45 grain-bg" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            {logo ? (
              <div className="h-12 w-12 rounded-md bg-white flex items-center justify-center overflow-hidden">
                <img src={logo} alt="logo" className="h-full w-full object-contain" />
              </div>
            ) : (
              <div className="h-11 w-11 rounded-md bg-secondary flex items-center justify-center">
                <Leaf className="h-6 w-6" />
              </div>
            )}
            <span className="font-display font-bold text-lg tracking-tight">Neighborhood Lawn Care</span>
          </div>
          <div>
            <div className="overline text-white/70 mb-3">All-in-one operations</div>
            <h2 className="font-display text-4xl xl:text-5xl font-black leading-none tracking-tight max-w-md">
              Schedule crews. Win jobs. Get paid faster.
            </h2>
            <p className="mt-5 text-white/80 leading-relaxed max-w-md">
              Your complete CRM for scheduling, invoicing, and Wave accounting sync — built for the field.
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            {logo ? (
              <div className="h-10 w-10 rounded-md bg-white border border-border flex items-center justify-center overflow-hidden">
                <img src={logo} alt="logo" className="h-full w-full object-contain" />
              </div>
            ) : (
              <div className="h-10 w-10 rounded-md bg-primary flex items-center justify-center">
                <Leaf className="h-5 w-5 text-secondary" />
              </div>
            )}
            <span className="font-display font-bold tracking-tight">Neighborhood Lawn Care</span>
          </div>
          <div className="overline text-secondary mb-2">Welcome back</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight mb-8">Sign in to your CRM</h1>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@gmail.com"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>
            {error && (
              <p data-testid="login-error" className="text-sm text-destructive">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-primary text-primary-foreground hover:bg-[#152e17] hover:text-white transition-colors duration-200"
            >
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          <p className="mt-8 text-xs text-muted-foreground leading-relaxed">
            Staff accounts are created by your administrator under Team. Contact your admin if you need access.
          </p>
        </div>
      </div>
    </div>
  );
}
